import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserPlus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Field, Input, useToast } from '@/components/ui';
import { ApiError, request } from '@/lib/api';
import { customerSchema } from '@/lib/schemas';

/**
 * לקוח חדש בלי לצאת מהמסך (הצעת מחיר). אותו POST /customers כמו במסך
 * הלקוחות. המיקום (למרחקים בשיבוץ) נשמר בביקור הראשון אצל הלקוח.
 *
 * טלפון או מייל — חובה אחד מהם: ההצעה נשלחת בוואטסאפ (טלפון), והעותק
 * החתום אחרי האישור — במייל או בוואטסאפ. בלי אף אחד אין לאן לשלוח.
 */
export function InlineNewCustomer({
  initialName,
  onCreated,
  onCancel,
}: {
  initialName: string;
  onCreated: (c: { id: string; name: string; email: string | null; phone: string | null }) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation(['customers', 'common']);
  const toast = useToast();
  const qc = useQueryClient();
  const [v, setV] = useState({ name: initialName, phone: '', email: '', address: '' });
  const emailInvalid = v.email.trim() !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email.trim());
  const noContact = !v.phone.trim() && !v.email.trim();
  // מה חוסם את ההוספה — מוצג ליד הכפתור, לא רק כפתור אפור.
  const blockers = [
    !v.name.trim() ? t('inline.nameRequired') : null,
    noContact ? t('inline.contactRequired') : null,
    emailInvalid ? t('inline.emailInvalid') : null,
  ].filter((x): x is string => x !== null);

  const create = useMutation({
    mutationFn: () =>
      request('/customers', {
        method: 'POST',
        body: {
          name: v.name.trim(),
          ...(v.phone.trim() ? { phone: v.phone.trim() } : {}),
          ...(v.email.trim() ? { email: v.email.trim() } : {}),
          ...(v.address.trim() ? { address: v.address.trim() } : {}),
        },
        schema: customerSchema,
      }),
    onSuccess: async (c) => {
      toast.success(t('inline.added'), c.name);
      await qc.invalidateQueries({ queryKey: ['customers'] });
      onCreated({ id: c.id, name: c.name, email: c.email ?? null, phone: c.phone ?? null });
    },
    onError: (e) => toast.error(t('inline.failed'), e instanceof ApiError ? e.message : undefined),
  });

  const set = (k: keyof typeof v) => (e: React.ChangeEvent<HTMLInputElement>) => setV({ ...v, [k]: e.target.value });

  return (
    <section className="space-y-3 rounded-(--radius-lg) border border-accent-border bg-accent-subtle/40 p-3" aria-label={t('inline.title')}>
      <div className="flex items-center gap-2">
        <UserPlus className="size-4 text-accent" aria-hidden />
        <h3 className="text-sm font-medium text-fg">{t('inline.title')}</h3>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('form.name')} htmlFor="nc-name" required>
          <Input id="nc-name" autoFocus maxLength={200} value={v.name} onChange={set('name')} />
        </Field>
        <Field label={t('form.phone')} htmlFor="nc-phone" hint={t('inline.phoneHint')} required={!v.email.trim()}>
          <Input id="nc-phone" type="tel" dir="ltr" inputMode="tel" maxLength={40} value={v.phone} onChange={set('phone')} />
        </Field>
        <Field
          label={t('inline.email')}
          htmlFor="nc-email"
          hint={t('inline.emailHint')}
          required={!v.phone.trim()}
          error={emailInvalid ? t('inline.emailInvalid') : undefined}
        >
          <Input id="nc-email" type="email" dir="ltr" maxLength={320} value={v.email} onChange={set('email')} />
        </Field>
        <Field label={t('form.address')} htmlFor="nc-address" hint={t('inline.addressHint')}>
          <Input id="nc-address" maxLength={500} value={v.address} onChange={set('address')} />
        </Field>
      </div>
      {noContact ? <p className="text-xs text-fg-muted">{t('inline.contactRequired')}</p> : null}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          disabled={blockers.length > 0}
          aria-describedby={blockers.length > 0 ? 'nc-blockers' : undefined}
          loading={create.isPending}
          onClick={() => create.mutate()}
        >
          {t('inline.submit')}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          {t('common:actions.cancel')}
        </Button>
        {blockers.length > 0 ? (
          <span id="nc-blockers" className="sr-only">
            {blockers.join('. ')}
          </span>
        ) : null}
      </div>
    </section>
  );
}
